/**
 * iter141 — Fleet & Equipment Usage Timeline
 *
 * Answers "when did we last use this truck / drill, and what did we bill for it?"
 * Renders a chronologically-sorted list of every quote / invoice / schedule
 * this resource has been attached to.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, FileText, Receipt, Calendar, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_ICON = { quote: FileText, invoice: Receipt, schedule: Calendar };
const KIND_LABEL = { quote: "Quote", invoice: "Invoice", schedule: "Job" };

export default function ResourceUsageTimeline({ kind }) {
  // kind: 'fleet' | 'equipment'
  const params = useParams();
  const resourceId = params.vehicleId || params.equipmentId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resourceName, setResourceName] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const base = kind === "fleet" ? `${API}/fleet/vehicles` : `${API}/equipment`;
        const [tRes, rRes] = await Promise.all([
          axios.get(`${base}/${resourceId}/timeline`),
          axios.get(`${base}/${resourceId}`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setData(tRes.data);
        const r = rRes.data;
        setResourceName(
          r?.name ||
            [r?.year, r?.make, r?.model].filter(Boolean).join(" ") ||
            "Resource"
        );
      } catch {
        if (!cancelled) setData({ events: [], totals: {} });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [kind, resourceId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const events = data?.events || [];
  const totals = data?.totals || {};
  const backTo = kind === "fleet" ? "/fleet" : "/equipment";
  const currency = (n) =>
    n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="resource-usage-timeline">
      <div className="flex items-center gap-4">
        <Button variant="ghost" asChild>
          <Link to={backTo}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">
          {resourceName} — Usage Timeline
        </h1>
        <p className="text-muted-foreground mt-1">
          Every quote, invoice and job this {kind === "fleet" ? "vehicle" : "equipment item"} has been attached to.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Total events</p>
            <p className="text-2xl font-bold" data-testid="timeline-total-events">
              {totals.total_events ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Total quoted</p>
            <p className="text-2xl font-bold">{currency(totals.total_quoted)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Total billed</p>
            <p className="text-2xl font-bold text-emerald-600">
              {currency(totals.total_billed)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Last used</p>
            <p className="text-2xl font-bold">{totals.last_used || "—"}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Activity</CardTitle>
          <CardDescription>Newest first</CardDescription>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-center text-muted-foreground py-12" data-testid="timeline-empty">
              This {kind === "fleet" ? "vehicle" : "equipment item"} hasn't been attached to any quotes, invoices, or jobs yet.
            </p>
          ) : (
            <div className="space-y-3">
              {events.map((e) => {
                const Icon = KIND_ICON[e.kind] || FileText;
                return (
                  <Link
                    key={`${e.kind}-${e.id}`}
                    to={e.link}
                    className="flex items-center gap-4 rounded-lg border bg-card p-4 hover:bg-muted/50 transition-colors"
                    data-testid={`timeline-row-${e.kind}-${e.id}`}
                  >
                    <div className="rounded-full bg-primary/10 p-2 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{KIND_LABEL[e.kind]} · {e.reference}</span>
                        <Badge variant="outline">{e.status}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {e.customer || "—"} · {e.date || "no date"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{currency(e.amount)}</p>
                      {e.balance_due != null && e.balance_due > 0 && (
                        <p className="text-xs text-red-500">
                          {currency(e.balance_due)} due
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
